import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { Project, Task, ProjectMember } from '../types';

export class ProjectService {
  private static instance: ProjectService;

  static getInstance(): ProjectService {
    if (!ProjectService.instance) {
      ProjectService.instance = new ProjectService();
    }
    return ProjectService.instance;
  }

  // Project CRUD Operations
  async createProject(projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'projects'), {
        ...projectData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Project created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating project:', error);
      throw error;
    }
  }

  async updateProject(projectId: string, updates: Partial<Project>): Promise<void> {
    try {
      const projectRef = doc(db, 'projects', projectId);
      
      // Clean the drive folder data if present - only keep essential fields for Firestore
      const cleanUpdates = { ...updates };
      if (cleanUpdates.driveFolder) {
        cleanUpdates.driveFolder = {
          id: cleanUpdates.driveFolder.id,
          name: cleanUpdates.driveFolder.name,
          url: cleanUpdates.driveFolder.url
        };
      }
      
      await updateDoc(projectRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Project updated:', projectId);
    } catch (error) {
      console.error('❌ Error updating project:', error);
      throw error;
    }
  }

  async deleteProject(projectId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Delete the project
      const projectRef = doc(db, 'projects', projectId);
      batch.delete(projectRef);

      // Delete all tasks in the project
      const tasksQuery = query(collection(db, 'tasks'), where('projectId', '==', projectId));
      const tasksSnapshot = await getDocs(tasksQuery);
      tasksSnapshot.forEach((taskDoc) => {
        batch.delete(taskDoc.ref);
      });

      // Delete all project members
      const membersQuery = query(collection(db, 'projectMembers'), where('projectId', '==', projectId));
      const membersSnapshot = await getDocs(membersQuery);
      membersSnapshot.forEach((memberDoc) => {
        batch.delete(memberDoc.ref);
      });

      await batch.commit();
      console.log('✅ Project and related data deleted:', projectId);
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      throw error;
    }
  }

  async getProject(projectId: string): Promise<Project | null> {
    try {
      const projectDoc = await getDoc(doc(db, 'projects', projectId));
      
      if (!projectDoc.exists()) {
        return null;
      }

      const data = projectDoc.data();
      return {
        id: projectDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as Project;
    } catch (error) {
      console.error('❌ Error getting project:', error);
      throw error;
    }
  }

  async getUserProjects(userId: string): Promise<Project[]> {
    try {
      // Get projects owned by user
      const ownedQuery = query(
        collection(db, 'projects'),
        where('ownerId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      const ownedSnapshot = await getDocs(ownedQuery);

      // Get projects where user is a member
      const membersQuery = query(
        collection(db, 'projectMembers'),
        where('userId', '==', userId)
      );
      const membersSnapshot = await getDocs(membersQuery);
      
      const memberProjectIds = membersSnapshot.docs.map(doc => doc.data().projectId);
      
      let sharedProjects: Project[] = [];
      if (memberProjectIds.length > 0) {
        // Firestore 'in' queries are limited to 10 items, so we might need multiple queries
        const chunks = [];
        for (let i = 0; i < memberProjectIds.length; i += 10) {
          chunks.push(memberProjectIds.slice(i, i + 10));
        }

        for (const chunk of chunks) {
          const sharedQuery = query(
            collection(db, 'projects'),
            where('__name__', 'in', chunk.map(id => doc(db, 'projects', id)))
          );
          const sharedSnapshot = await getDocs(sharedQuery);
          
          sharedSnapshot.forEach(doc => {
            const data = doc.data();
            sharedProjects.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
              updatedAt: data.updatedAt?.toDate() || new Date()
            } as Project);
          });
        }
      }

      // Combine owned and shared projects, removing duplicates
      const ownedProjects = ownedSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Project;
      });

      const allProjects = [...ownedProjects, ...sharedProjects];
      const uniqueProjects = allProjects.filter((project, index, self) => 
        index === self.findIndex(p => p.id === project.id)
      );

      return uniqueProjects.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    } catch (error) {
      console.error('❌ Error getting user projects:', error);
      throw error;
    }
  }

  // Task CRUD Operations
  async createTask(taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        dueDate: taskData.dueDate ? Timestamp.fromDate(taskData.dueDate) : null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      
      console.log('✅ Task created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating task:', error);
      throw error;
    }
  }

  async updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      
      // Clean updates - filter out undefined values for Firestore
      const cleanUpdates: any = {};
      
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        if (value !== undefined) {
          cleanUpdates[key] = value;
        }
      });
      
      const updateData: any = {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      };

      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate);
      }

      await updateDoc(taskRef, updateData);
      console.log('✅ Task updated:', taskId);
    } catch (error) {
      console.error('❌ Error updating task:', error);
      throw error;
    }
  }

  async deleteTask(taskId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
      console.log('✅ Task deleted:', taskId);
    } catch (error) {
      console.error('❌ Error deleting task:', error);
      throw error;
    }
  }

  async getProjectTasks(projectId: string): Promise<Task[]> {
    try {
      const tasksQuery = query(
        collection(db, 'tasks'),
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(tasksQuery);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dueDate: data.dueDate?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Task;
      });
    } catch (error) {
      console.error('❌ Error getting project tasks:', error);
      throw error;
    }
  }

  // Project Member Operations
  async addProjectMember(projectId: string, userId: string, role: ProjectMember['role']): Promise<void> {
    try {
      await addDoc(collection(db, 'projectMembers'), {
        projectId,
        userId,
        role,
        addedAt: Timestamp.now()
      });
      
      console.log('✅ Project member added:', { projectId, userId, role });
    } catch (error) {
      console.error('❌ Error adding project member:', error);
      throw error;
    }
  }

  async updateProjectMemberRole(projectId: string, userId: string, role: ProjectMember['role']): Promise<void> {
    try {
      const membersQuery = query(
        collection(db, 'projectMembers'),
        where('projectId', '==', projectId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(membersQuery);

      if (!snapshot.empty) {
        const memberRef = snapshot.docs[0].ref;
        await updateDoc(memberRef, { role });
        console.log('✅ Project member role updated:', { projectId, userId, role });
      }
    } catch (error) {
      console.error('❌ Error updating project member role:', error);
      throw error;
    }
  }

  async removeProjectMember(projectId: string, userId: string): Promise<void> {
    try {
      const membersQuery = query(
        collection(db, 'projectMembers'),
        where('projectId', '==', projectId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(membersQuery);

      if (!snapshot.empty) {
        await deleteDoc(snapshot.docs[0].ref);
        console.log('✅ Project member removed:', { projectId, userId });
      }
    } catch (error) {
      console.error('❌ Error removing project member:', error);
      throw error;
    }
  }

  async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
    try {
      const membersQuery = query(
        collection(db, 'projectMembers'),
        where('projectId', '==', projectId),
        orderBy('addedAt', 'desc')
      );
      const snapshot = await getDocs(membersQuery);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          addedAt: data.addedAt?.toDate() || new Date()
        } as ProjectMember;
      });
    } catch (error) {
      console.error('❌ Error getting project members:', error);
      throw error;
    }
  }

  async getUserProjectRole(projectId: string, userId: string): Promise<ProjectMember['role'] | 'owner' | null> {
    try {
      // First check if user is the owner
      const project = await this.getProject(projectId);
      if (project?.ownerId === userId) {
        return 'owner';
      }

      // Check if user is a member
      const membersQuery = query(
        collection(db, 'projectMembers'),
        where('projectId', '==', projectId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(membersQuery);

      if (!snapshot.empty) {
        return snapshot.docs[0].data().role as ProjectMember['role'];
      }

      return null;
    } catch (error) {
      console.error('❌ Error getting user project role:', error);
      throw error;
    }
  }
}
